import { UAContext, UserAgent } from '@quentin-sommer/react-useragent';
import _ from 'lodash';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Layout from '~components/layout';
import { PropertyCard, ResidenceLoading } from '~components/residences';
import ScrollAbleTabs from '~components/scrollable-tabs';
import { useThemeContext } from '~components/theme-provider';
import Typography from '~components/typography';
import { Router, useTranslation } from '~i18n';
import styles from '~public/styles/residences.module.scss';

type CountryType = {
  name: string | undefined | null;
  id: string | undefined | null;
};

const Residences: NextPage = () => {
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { t } = useTranslation(['student_portal']);
  const router = useRouter();
  const { propertyWithEnabledAcademicYear } = useThemeContext();

  const { country } = router.query;

  const [countries, setCountries] = useState<CountryType[]>([]);

  const [countryId, setCountryId] = useState(country);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (country !== countryId) {
      setCountryId(country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, setCountryId]);

  useEffect(() => {
    if (!propertyWithEnabledAcademicYear.length) return;

    const propertyCountry = propertyWithEnabledAcademicYear?.map(item => {
      return {
        id: item?.country?.id,
        name: item?.country?.name,
      };
    });

    const landlordCountries = _.uniqWith(propertyCountry, _.isEqual);
    landlordCountries.unshift({
      id: '',
      name: 'All',
    });

    const order = [
      'All',
      'United Kingdom',
      'Ireland',
      'Germany',
      'Spain',
      'Australia',
      'Japan',
      'United Arab Emirates',
      'Austria',
      'United States of America',
    ];

    landlordCountries.sort((pre: any, next: any) => {
      const preIndex = pre.name === 'All' ? -1 : order.indexOf(pre.name);
      const nextIndex = next.name === 'All' ? -1 : order.indexOf(next.name);

      if (preIndex === -1 && nextIndex === -1 && pre.name !== 'All' && next.name !== 'All') {
        return pre.name.localeCompare(next.name);
      }
      if (preIndex === -1 && pre.name !== 'All') return 1;
      if (nextIndex === -1 && next.name !== 'All') return -1;

      return preIndex - nextIndex;
    });

    setCountries(landlordCountries);
    setLoading(false);
  }, [propertyWithEnabledAcademicYear]);

  const getProperties = useCallback(() => {
    if (countryId) {
      const list = propertyWithEnabledAcademicYear.filter(item => {
        return item?.country?.id === countryId;
      });
      return list.sort((current, next) => {
        return current.name.toLowerCase() > next.name.toLowerCase() ? 1 : -1;
      });
    } else {
      return propertyWithEnabledAcademicYear.sort((current, next) => {
        return current.name.toLowerCase() > next.name.toLowerCase() ? 1 : -1;
      }); // change to properties in the future
    }
  }, [countryId, propertyWithEnabledAcademicYear]);

  const changeCountry = useCallback(
    index => {
      const currentCountryId = countries[index].id;
      if (currentCountryId) {
        const url = `${window.location.origin}${window.location.pathname}?country=${currentCountryId}`;
        window.history.pushState(null, '', url);
        setCountryId(currentCountryId);
      } else {
        setCountryId('');
        const url = `${window.location.origin}${window.location.pathname}`;
        window.history.pushState(null, '', url);
      }
    },
    [countries]
  );

  const getIndex = useCallback(() => {
    if (!countryId) return 0;
    return countries.findIndex(item => {
      return item.id === countryId;
    });
  }, [countries, countryId]);

  return (
    <Layout layoutFlexible={true}>
      <div className={styles.root}>
        {loading && <ResidenceLoading />}
        {!loading && (
          <>
            <Typography
              component="div"
              variant={isMobile ? 'h7' : 'h2'}
              style={{ marginBottom: isMobile ? 16 : 28 }}
            >
              {t('redidence.all')}
            </Typography>
            <ScrollAbleTabs
              tabs={countries}
              tabIndex={getIndex()}
              onChange={(index: number) => {
                changeCountry(index);
              }}
            />
            <div className={styles.properties}>
              {getProperties()?.map((item, index) => {
                return (
                  <PropertyCard
                    className={styles.propertiesItem}
                    key={index}
                    propertyId={item?.id}
                    name={item?.name}
                    imageName={item?.imageName}
                    cityName={item?.city?.name}
                    handleClick={(id: string) => Router.push(`/filter?propertyId=${id}`)}
                  />
                );
              })}
              <div className={styles.propertiesItemSection}></div>
              <div className={styles.propertiesItemSection}></div>
              <div className={styles.propertiesItemSection}></div>
              <UserAgent computer>
                <div className={styles.propertiesItemSection}></div>
              </UserAgent>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
Residences.getInitialProps = async () => ({
  namespacesRequired: ['public', 'student_portal'],
});

export default Residences;
